/**
 * completedChapterSignal — web port of the iOS chapter-completion hand-off
 * (client-native/src/stores/globalStore.ts:320-365 emitCompletedChapterSignal /
 * completedChapterSignal).
 *
 * Lesson/module flows emit the signal when the user finishes a chapter (and
 * optionally the whole course); the Home path (ChapterPathList) peeks it on
 * mount to run the celebration — next-chapter glow, or the CourseCompletedPopup
 * when courseCompleted is set. Backed by sessionStorage (iOS uses an in-memory
 * observable) so it survives the lesson → home route transition and any
 * incidental reload in between, but never leaks across browser sessions.
 *
 * How to verify: from the console run
 *   sessionStorage.setItem("parrot.completedChapterSignal",
 *     JSON.stringify({ chapterId: <id>, title: "Chapter", isFlightSchool: false, courseCompleted: true }))
 * then navigate to /home/learn — the Congrats popup appears after the
 * celebration beat (or the next chapter glows when courseCompleted is false).
 */

export type ChapterCompletionId = number | string;

export interface CompletedChapterSignal {
  chapterId: ChapterCompletionId;
  title: string;
  isFlightSchool: boolean;
  courseCompleted?: boolean;
  isFirstCompletion?: boolean;
}

export interface EmitCompletedChapterSignalParams {
  chapterId: ChapterCompletionId;
  title?: string;
  isFlightSchool: boolean;
  courseCompleted?: boolean;
  isFirstCompletion?: boolean;
}

const STORAGE_KEY = "parrot.completedChapterSignal";

export function emitCompletedChapterSignal({
  chapterId,
  title,
  isFlightSchool,
  courseCompleted = false,
  isFirstCompletion = true,
}: EmitCompletedChapterSignalParams): void {
  const signal: CompletedChapterSignal = {
    chapterId,
    title: title ?? (isFlightSchool ? "Flight School" : "Chapter"),
    isFlightSchool,
    courseCompleted,
    isFirstCompletion,
  };
  try {
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(signal));
  } catch {
    // Storage unavailable (private mode quota etc.) — celebration is skipped.
  }
}

export function peekCompletedChapterSignal(): CompletedChapterSignal | null {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as CompletedChapterSignal;
    if (parsed == null || parsed.chapterId == null) return null;
    return parsed;
  } catch {
    return null;
  }
}

export function clearCompletedChapterSignal(): void {
  try {
    sessionStorage.removeItem(STORAGE_KEY);
  } catch {
    // ignore
  }
}

// iOS globalStore.ts:335-349 — ids compare as strings so number/string sources
// (API chapter ids vs FS module slugs) match consistently.
function getChapterCompletionKey(
  chapterId: ChapterCompletionId | null | undefined,
): string | null {
  if (chapterId === null || chapterId === undefined) return null;
  return String(chapterId);
}

export function chapterCompletionIdsMatch(
  leftId: ChapterCompletionId | null | undefined,
  rightId: ChapterCompletionId | null | undefined,
): boolean {
  const left = getChapterCompletionKey(leftId);
  const right = getChapterCompletionKey(rightId);
  return !!left && !!right && left === right;
}
